import React from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, ExternalLink } from 'lucide-react';
import { RelatedOpenSaleOrder } from '../../../services/legacyApi';

interface RelatedOpenSaleOrdersNoticeProps {
    orders: RelatedOpenSaleOrder[];
    customerName?: string;
}

const formatDate = (value?: string) => {
    if (!value) return '-';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? '-' : date.toLocaleDateString();
};

const formatQty = (value?: number) => Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 3 });

export const RelatedOpenSaleOrdersNotice: React.FC<RelatedOpenSaleOrdersNoticeProps> = ({ orders, customerName }) => {
    if (!orders.length) return null;

    return (
        <div className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-950">
            <div className="mb-3 flex items-start gap-2">
                <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-700" />
                <div>
                    <div className="font-semibold">This customer has other SOs pending dispatch.</div>
                    <div className="text-xs text-amber-800">
                        Check related commitments before creating or dispatching against this LPO{customerName ? ` for ${customerName}` : ''}.
                    </div>
                </div>
            </div>
            <div className="overflow-x-auto rounded border border-amber-200 bg-white">
                <table className="w-full min-w-[680px] text-xs">
                    <thead className="bg-amber-100/70 text-amber-900">
                        <tr>
                            <th className="px-3 py-2 text-left font-medium">SO</th>
                            <th className="px-3 py-2 text-left font-medium">Customer PO</th>
                            <th className="px-3 py-2 text-left font-medium">SO Date</th>
                            <th className="px-3 py-2 text-left font-medium">Dispatch</th>
                            <th className="px-3 py-2 text-right font-medium">Pending Qty</th>
                            <th className="px-3 py-2 text-right font-medium">Action</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-amber-100">
                        {orders.map((order) => (
                            <tr key={order.so_id}>
                                <td className="px-3 py-2 font-mono">{order.so_number || `HC/SO/${String(order.so_id).padStart(4, '0')}`}</td>
                                <td className="px-3 py-2">{order.customer_po_no || '-'}</td>
                                <td className="px-3 py-2">{formatDate(order.so_date)}</td>
                                <td className="px-3 py-2">{order.dispatch_progress_name || 'Not Dispatched'}</td>
                                <td className="px-3 py-2 text-right font-semibold">{formatQty(order.pending_dispatch_qty)}</td>
                                <td className="px-3 py-2 text-right">
                                    <Link
                                        to={`/legacy/orders/${order.so_id}`}
                                        className="inline-flex items-center gap-1 font-medium text-amber-900 hover:underline"
                                    >
                                        Open <ExternalLink className="h-3 w-3" />
                                    </Link>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};
